import { useEffect, useRef } from "react";
import { useConversation } from "@api/agentChat.api";
import type { AgentChatLiveResponseState } from "@api/agentChatStream.api";
import { AgentChatAttachmentChip } from "./AgentChatAttachmentChip";
import { AgentChatLiveResponse } from "./AgentChatLiveResponse";
import { AgentChatMessage } from "./AgentChatMessage";
import styles from "./AgentChat.module.scss";

export interface PendingAttachmentDescriptor {
    fileName: string;
    mimeType: string;
}

export interface PendingSubmission {
    content: string;
    attachments: PendingAttachmentDescriptor[];
}

interface Props {
    conversationId: string | null;
    workspaceId: string;
    onFollowUpSelected: (suggestion: string) => void;
    pendingSubmission?: PendingSubmission | null;
    liveResponse?: AgentChatLiveResponseState | null;
}

export const AgentChatThread: React.FC<Props> = ({
    conversationId,
    workspaceId,
    onFollowUpSelected,
    pendingSubmission,
    liveResponse,
}) => {
    const conversation = useConversation(conversationId, workspaceId);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const serverMessageCount = conversation.data?.messages.length ?? 0;
    const showPending = Boolean(pendingSubmission);
    // Keep auto-scroll pinned to the bottom as live tool rows stream in.
    const hasLiveResponse = Boolean(liveResponse);
    const liveRowCount = liveResponse?.rows.size ?? 0;
    const hasLiveIntent = Boolean(liveResponse?.intent);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [
        serverMessageCount,
        showPending,
        hasLiveResponse,
        liveRowCount,
        hasLiveIntent,
    ]);

    // For brand new conversations (no conversationId yet) we render a minimal
    // thread shell that contains the pending row and the live response — the
    // server has no history to fetch yet.
    if (!conversationId) {
        if (!pendingSubmission && !liveResponse) {
            return null;
        }
        return (
            <div className={styles.thread}>
                {pendingSubmission && (
                    <PendingUserTurn submission={pendingSubmission} />
                )}
                {liveResponse && (
                    <AgentChatLiveResponse liveResponse={liveResponse} />
                )}
                <div ref={bottomRef} />
            </div>
        );
    }

    if (conversation.isPending) {
        return <div className={styles.threadLoading}>Loading…</div>;
    }
    if (!conversation.data) {
        return null;
    }

    return (
        <div className={styles.thread}>
            {conversation.data.messages.map((message) => (
                <AgentChatMessage
                    key={message.id}
                    message={message}
                    onFollowUpSelected={onFollowUpSelected}
                />
            ))}
            {pendingSubmission && (
                <PendingUserTurn submission={pendingSubmission} />
            )}
            {liveResponse && (
                <AgentChatLiveResponse liveResponse={liveResponse} />
            )}
            <div ref={bottomRef} />
        </div>
    );
};

const PendingUserTurn: React.FC<{ submission: PendingSubmission }> = ({
    submission,
}) => (
    <div className={styles.userTurn}>
        {submission.attachments.length > 0 && (
            <div className={styles.attachmentRow}>
                {submission.attachments.map((attachment, index) => (
                    <AgentChatAttachmentChip
                        key={index}
                        fileName={attachment.fileName}
                        mimeType={attachment.mimeType}
                        mode="static"
                    />
                ))}
            </div>
        )}
        <div className={styles.userBubble}>{submission.content}</div>
    </div>
);
